export const STRATEGY_EDITOR_SECTION_IDS = ['overview', 'setup', 'entry', 'exit', 'position', 'backtests', 'activity'] as const;

export type StrategyEditorSectionId = (typeof STRATEGY_EDITOR_SECTION_IDS)[number];

export interface StrategyEditorSection {
    id: StrategyEditorSectionId;
    label: string;
    /** Sections that read persisted state are meaningless until the strategy exists on the backend. */
    requiresPersistedStrategy: boolean;
    /** Backtest and journal surfaces resolve owner-scoped data and stay hidden for shared strategies. */
    requiresOwnership: boolean;
}

export const STRATEGY_EDITOR_SECTIONS: StrategyEditorSection[] = [
    { id: 'overview', label: 'Overview', requiresPersistedStrategy: true, requiresOwnership: false },
    { id: 'setup', label: 'Setup', requiresPersistedStrategy: false, requiresOwnership: false },
    { id: 'entry', label: 'Entry', requiresPersistedStrategy: false, requiresOwnership: false },
    { id: 'exit', label: 'Exit', requiresPersistedStrategy: false, requiresOwnership: false },
    { id: 'position', label: 'Position', requiresPersistedStrategy: false, requiresOwnership: false },
    // Reachable while creating too: backtest option buying strategies are required for option legs.
    { id: 'backtests', label: 'Backtests', requiresPersistedStrategy: false, requiresOwnership: true },
    { id: 'activity', label: 'Activity', requiresPersistedStrategy: true, requiresOwnership: false }
];

export const isStrategyEditorSectionId = (value: string | null | undefined): value is StrategyEditorSectionId =>
    value !== null && value !== undefined && STRATEGY_EDITOR_SECTION_IDS.some((sectionId) => sectionId === value);

export interface StrategyEditorSectionVisibilityInput {
    isEditMode: boolean;
    isOwnedStrategy: boolean;
    hasPersistedStrategy: boolean;
}

export const getVisibleStrategyEditorSections = ({
    isEditMode,
    isOwnedStrategy,
    hasPersistedStrategy
}: StrategyEditorSectionVisibilityInput): StrategyEditorSection[] =>
    STRATEGY_EDITOR_SECTIONS.filter((section) => {
        if (section.requiresPersistedStrategy && !(isEditMode && hasPersistedStrategy)) return false;
        if (section.requiresOwnership && !isOwnedStrategy) return false;

        return true;
    });

/**
 * Resolves the section to render. Falls back to the first visible section so a stale `?section=`
 * value or a permission change can never leave the editor showing an empty pane.
 */
export const resolveActiveStrategyEditorSection = (
    requestedSectionId: string | null | undefined,
    visibleSections: StrategyEditorSection[]
): StrategyEditorSectionId => {
    const fallbackSectionId = visibleSections[0]?.id ?? 'setup';

    if (!isStrategyEditorSectionId(requestedSectionId)) return fallbackSectionId;

    return visibleSections.some((section) => section.id === requestedSectionId) ? requestedSectionId : fallbackSectionId;
};
